import type {
  DeliveryArea,
  MenuCategory,
  MenuItem,
  MenuOptionGroup,
  Rating,
  DineinFloor,
} from "@/types";
import type { GiftCard, Offer, Voucher } from "@/types/wallet";
import { NO_DISPLAY_IMAGE } from "../constants/images";

/**
 * Demo menu for the mock API. Prices are in PKR, matching the tenant's
 * `currency_code`. Swap this file out — or point the app at a real backend
 * via NEXT_PUBLIC_API_BASE_URL — when you go live.
 */

const IMG =
  NO_DISPLAY_IMAGE;

export const categories: MenuCategory[] = [
  { _id: "cat-popular", name: "Popular", sort_order: 0 },
  { _id: "cat-smash", name: "Smash Burgers", sort_order: 1 },
  { _id: "cat-fries", name: "Loaded Fries", sort_order: 2 },
  { _id: "cat-sides", name: "Sides", sort_order: 3 },
  { _id: "cat-drinks", name: "Beverages", sort_order: 4 },
  { _id: "cat-desserts", name: "Desserts", sort_order: 5 },
];

const pattyGroup: MenuOptionGroup = {
  _id: "grp-patty",
  name: "Choose your patty",
  required: true,
  min_selection: 1,
  max_selection: 1,
  options: [
    { _id: "opt-single", name: "Single patty", price: 0 },
    { _id: "opt-double", name: "Double patty", price: 450 },
    { _id: "opt-triple", name: "Triple patty", price: 850 },
  ],
};

const addOnGroup: MenuOptionGroup = {
  _id: "grp-addons",
  name: "Add extras",
  required: false,
  min_selection: 0,
  max_selection: 5,
  options: [
    { _id: "opt-cheese", name: "Extra cheese slice", price: 120 },
    { _id: "opt-jalapeno", name: "Jalapeños", price: 90 },
    { _id: "opt-bacon", name: "Beef bacon", price: 260 },
    { _id: "opt-egg", name: "Fried egg", price: 150 },
    { _id: "opt-onion", name: "Caramelised onions", price: 100 },
  ],
};

const spiceGroup: MenuOptionGroup = {
  _id: "grp-spice",
  name: "Spice level",
  required: true,
  min_selection: 1,
  max_selection: 1,
  options: [
    { _id: "opt-mild", name: "Mild", price: 0 },
    { _id: "opt-medium", name: "Medium", price: 0 },
    { _id: "opt-hot", name: "Hot 🔥", price: 0 },
  ],
};

const drinkSizeGroup: MenuOptionGroup = {
  _id: "grp-size",
  name: "Size",
  required: true,
  min_selection: 1,
  max_selection: 1,
  options: [
    { _id: "opt-regular", name: "Regular", price: 0 },
    { _id: "opt-large", name: "Large", price: 150 },
  ],
};

export const items: MenuItem[] = [
  {
    _id: "itm-classic",
    category_id: "cat-smash",
    name: "The Classic SMASHD",
    description:
      "Two smashed beef patties, American cheese, pickles, house sauce, toasted potato bun.",
    price: 1150,
    image_url: IMG,
    option_groups: [pattyGroup, addOnGroup],
    featured: true,
  },
  {
    _id: "itm-double-trouble",
    category_id: "cat-smash",
    name: "Double Trouble",
    description: "Double patty, double cheese, crispy onions and smoky mayo.",
    price: 1450,
    image_url: IMG,
    option_groups: [addOnGroup],
    featured: true,
  },
  {
    _id: "itm-firecracker",
    category_id: "cat-smash",
    name: "Firecracker Smash",
    description:
      "Jalapeño, pepper jack, chipotle aioli. Built for heat seekers.",
    price: 1350,
    image_url: IMG,
    option_groups: [spiceGroup, pattyGroup, addOnGroup],
  },
  {
    _id: "itm-chicken",
    category_id: "cat-smash",
    name: "Crispy Chicken Smash",
    description: "Buttermilk-fried chicken thigh, slaw, garlic mayo.",
    price: 1250,
    image_url: IMG,
    option_groups: [spiceGroup, addOnGroup],
  },
  {
    _id: "itm-loaded-cheese",
    category_id: "cat-fries",
    name: "Loaded Cheese Fries",
    description: "Skin-on fries drowned in cheese sauce with spring onion.",
    price: 750,
    image_url: IMG,
    option_groups: [addOnGroup],
    featured: true,
  },
  {
    _id: "itm-loaded-beef",
    category_id: "cat-fries",
    name: "Beef Chilli Fries",
    description: "Slow-cooked beef chilli, cheese sauce, sour cream.",
    price: 950,
    image_url: IMG,
    option_groups: [],
  },
  {
    _id: "itm-fries",
    category_id: "cat-sides",
    name: "Skin-on Fries",
    description: "Golden, salted, dangerously moreish.",
    price: 400,
    image_url: IMG,
    option_groups: [],
  },
  {
    _id: "itm-onion-rings",
    category_id: "cat-sides",
    name: "Onion Rings",
    description: "Six beer-battered rings with dip.",
    price: 550,
    image_url: IMG,
    option_groups: [],
  },
  {
    _id: "itm-wings",
    category_id: "cat-sides",
    name: "Buffalo Wings (6 pcs)",
    description: "Tossed in buffalo sauce, served with ranch.",
    price: 890,
    image_url: IMG,
    option_groups: [spiceGroup],
  },
  {
    _id: "itm-cola",
    category_id: "cat-drinks",
    name: "Soft Drink",
    description: "Chilled bottle.",
    price: 200,
    image_url: IMG,
    option_groups: [drinkSizeGroup],
  },
  {
    _id: "itm-shake",
    category_id: "cat-drinks",
    name: "Thick Shake",
    description: "Vanilla, chocolate or salted caramel.",
    price: 650,
    image_url: IMG,
    option_groups: [
      {
        _id: "grp-shake-flavour",
        name: "Flavour",
        required: true,
        min_selection: 1,
        max_selection: 1,
        options: [
          { _id: "opt-vanilla", name: "Vanilla", price: 0 },
          { _id: "opt-choc", name: "Chocolate", price: 0 },
          { _id: "opt-caramel", name: "Salted caramel", price: 80 },
        ],
      },
      drinkSizeGroup,
    ],
    featured: true,
  },
  {
    _id: "itm-brownie",
    category_id: "cat-desserts",
    name: "Fudge Brownie",
    description: "Warm, gooey, served with vanilla ice cream.",
    price: 600,
    image_url: IMG,
    option_groups: [],
  },
];

/**
 * The "Popular" category is a virtual view over `featured` items, and the
 * tenant controls whether it appears at all via `web_show_featured_items`.
 * This previously injected it unconditionally, inventing a category the
 * back-office had switched off.
 */
export const resolveItems = (showFeatured = true): MenuItem[] =>
  showFeatured
    ? [
        ...items
          .filter((item) => item.featured)
          .map((item) => ({ ...item, category_id: "cat-popular" })),
        ...items,
      ]
    : items;

/** The featured category is named by the tenant (`featured_category_name`). */
export const resolveCategories = (
  showFeatured = true,
  featuredName = "Popular",
): MenuCategory[] =>
  showFeatured
    ? categories.map((category) =>
        category._id === "cat-popular"
          ? { ...category, name: featuredName }
          : category,
      )
    : categories.filter((category) => category._id !== "cat-popular");

/**
 * Offline fixtures for the wallet.
 *
 * These are `Offer` documents in the platform's REAL shape — the four-member
 * pathway enum and the seven-member reward enum — because the previous fixture
 * used reward types (`FREE_ITEM`, `GIFT_CARD`) and fields (`requires_items`,
 * `requires_voucher`, `voucher_code`) that exist nowhere in the schema.
 *
 * The live database holds NO offers, vouchers or gift cards for any tenant, so
 * these exist only to keep `NEXT_PUBLIC_USE_MOCK_API` useful offline. Nothing
 * reads them when the app is pointed at `:4001`.
 */
export const offers: Offer[] = [
  {
    _id: "off-freefries",
    name: "Free fries over Rs 2,000",
    description: "Spend Rs 2,000 or more and we will throw in skin-on fries.",
    active: true,
    included_platforms: ["WEB_DELIVERY", "WEB_TAKE-AWAY"],
    sort_id: 1,
    pathway_to_unlock_type: "ORDERS",
    pathway_to_unlock_type_orders: {
      min_num_of_orders: 0,
      total_amount: 2000,
      max_uses_per_customer: 1,
    },
    reward_type: "ITEMS",
    reward_items: { num_of_items: 1, item_ids: ["itm-fries"] },
  },
  {
    _id: "off-tuesday",
    name: "15% off on Tuesdays",
    description: "Automatically applied to takeaway orders every Tuesday.",
    active: true,
    auto_apply: true,
    included_platforms: ["WEB_TAKE-AWAY"],
    sort_id: 2,
    pathway_to_unlock_type: "ORDERS",
    pathway_to_unlock_type_orders: { min_num_of_orders: 0, total_amount: 0 },
    reward_type: "DISCOUNT",
    reward_discount: { discount_value: 15, is_discount_percentage: true },
  },
  {
    _id: "off-voucher",
    name: "SMASHD launch voucher",
    description: "Rs 500 off your next order.",
    active: true,
    included_platforms: ["WEB_DELIVERY", "WEB_TAKE-AWAY"],
    sort_id: 3,
    pathway_to_unlock_type: "VOUCHER",
    pathway_to_unlock_type_vouchers: { voucher_ids: ["vch-launch"] },
    reward_type: "DISCOUNT",
    reward_discount: { discount_value: 500 },
  },
];

export const vouchers: Voucher[] = [
  {
    _id: "vch-launch",
    name: "SMASHD launch voucher",
    code: "SMASHD500",
    active: true,
    expire_at: null,
  },
];

export const giftCards: GiftCard[] = [];

export const ratings: Rating[] = [
  {
    _id: "r1",
    rating: 5,
    comment: "Best smash burger in Islamabad, hands down.",
  },
  {
    _id: "r2",
    rating: 5,
    comment: "Fries were unreal. Delivery was quick too.",
  },
  {
    _id: "r3",
    rating: 4,
    comment: "Great burger, would like a bit more sauce.",
  },
  { _id: "r4", rating: 5, comment: "Ordered twice this week. No regrets." },
  { _id: "r5", rating: 4 },
  {
    _id: "r6",
    rating: 5,
    comment: "Firecracker smash is properly spicy. Love it.",
  },
];

export const deliveryAreas: DeliveryArea[] = [
  {
    _id: "area-dha2",
    name: "DHA Phase 2",
    delivery_cost: 150,
    min_order_value: 800,
    eta_minutes: 25,
  },
  {
    _id: "area-dha1",
    name: "DHA Phase 1",
    delivery_cost: 200,
    min_order_value: 1000,
    eta_minutes: 30,
  },
  {
    _id: "area-bahria",
    name: "Bahria Town Phase 4",
    delivery_cost: 250,
    min_order_value: 1200,
    eta_minutes: 35,
  },
  {
    _id: "area-gulberg",
    name: "Gulberg Greens",
    delivery_cost: 300,
    min_order_value: 1500,
    eta_minutes: 40,
  },
  {
    _id: "area-pwd",
    name: "PWD Housing Society",
    delivery_cost: 350,
    min_order_value: 1500,
    eta_minutes: 45,
  },
];

export const dineinLayout: DineinFloor[] = [
  {
    _id: "fl-ground",
    name: "Ground Floor",
    sort_id: 0,
    tables: [
      { _id: "tbl-1", name: "T1", num_of_seats: 2, num_of_guests: 0, order_id: null, seated_at: null },
      { _id: "tbl-2", name: "T2", num_of_seats: 4, num_of_guests: 0, order_id: null, seated_at: null },
      { _id: "tbl-3", name: "T3", num_of_seats: 4, num_of_guests: 2, order_id: null, seated_at: "2026-01-01T12:00:00.000Z" },
      { _id: "tbl-4", name: "T4", num_of_seats: 6, num_of_guests: 0, order_id: null, seated_at: null },
    ],
  },
  {
    _id: "fl-terrace",
    name: "Terrace",
    sort_id: 1,
    tables: [
      { _id: "tbl-5", name: "B1", num_of_seats: 2, num_of_guests: 0, order_id: null, seated_at: null },
      { _id: "tbl-6", name: "B2", num_of_seats: 4, num_of_guests: 0, order_id: null, seated_at: null },
      { _id: "tbl-7", name: "B3", num_of_seats: 8, num_of_guests: 4, order_id: null, seated_at: "2026-01-01T12:00:00.000Z" },
    ],
  },
];
